<template>
  <div class="transfersIn">
    <v-form>
      <v-row>
        <v-col cols="12">
          <v-btn
            depressed
            color="primary"
            :disabled="!canEditTransfer"
            @click="editTransfer(null)"
            >New Transfer</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="transfersList"
            item-key="TransfersInId"
            :items-per-page="-1"
            :loading="loading"
            loading-text="Loading transfers list..."
            :single-select="true"
            :calculate-widths="true"
            @click:row="editTransfer"
            class="searchResults"
            dense
            show-expand
          >
            <template v-for="header in headers" v-slot:[`item.${header.value}`]="{ item, value }">
              {{ formatTableEl(item, value, header.format) }}
            </template>
            <template v-slot:[`item.DateReceived`]="{ item }">
              <span>{{ displayDate(new Date(item.DateReceived)) }}</span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <TransferNotes :transfersInId="item.TransfersInId"></TransferNotes>
              </td>
            </template>
          </v-data-table>

          <Sidebar
            :isShown="isSidebarShown"
            :isNew="isNew"
            :formElements="formElements"
            :sidebarName="sidebarName"
            :isFormValid="isFormValid"
            :actionsDisabled="!canEditTransfer"
            @form-validity="isFormValid = $event"
            @delete="onDelete"
            @cancel="cancelChanges"
            @save="saveChanges"
          >
          </Sidebar>
        </v-col>
      </v-row>
    </v-form>
    <Dialog ref="confirmationDialog"></Dialog>
  </div>
</template>

<script>
import Sidebar from '@/components/layout/Sidebar.vue';
import Dialog from '@/components/layout/Dialog.vue';
import Utils from '@/utils.js';
import TransferNotes from './Components/TransferNotes.vue';

export default {
  components: { Sidebar, Dialog, TransferNotes },
  name: 'TransfersIn',

  data() {
    return {
      loading: false,
      isSidebarShown: false,
      sidebarName: "transfer in",
      formElements: [],
      allElements: [],
      headers: [],
      transfersList: [],
      selectedTransfer: null,
      isFormValid: false,
    };
  },

  computed: {
    memberId() {
      return this.$store.state.selectedMemberId;
    },
    isNew() {
      if (this.selectedTransfer) return false;
      return true;
    },
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },
    canEditTransfer() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    memberId() {
      this.getTransfersList();
    },
  },

  created() {
    this.getHeaders('TransfersIn');
  },

  mounted: function () {
    if (this.memberId) {
      this.getForm('TransfersIn');
      this.$root.currentPage = 'TransfersIn';
    } else {
      this.$store.commit('setNotificationMessage', {
        type: 'info',
        text: 'Please, select member',
      });
      this.$router.push({ path: '/' });
    }
  },

  methods: {
    displayDate(date) {
      if (date.getFullYear() <= 1900) return null;
      return Utils.getDateStr(date);
    },
    editTransfer(item) {
      if (!item && !this.canEditTransfer) return;
      this.isSidebarShown = true;
      if (item) {
        this.getTransferById(item.TransfersInId);
      } else {
        this.formElements.forEach((el) => (el.Value = ''));
        this.selectedTransfer = null;
      }
    },
    cancelChanges() {
      this.isSidebarShown = false;
      this.selectedTransfer = false;
    },
    saveChanges() {
      if (this.isFormValid) {
        this.isSidebarShown = false;
        if (this.isNew) {
          this.addTransfer();
        } else {
          this.updateTransfer();
        }
      }
    },
    async onDelete() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.isSidebarShown = false;
        this.deleteTransfer();
      } catch (err) {
        console.log();
      }
    },
    
    formatTableEl(item, value, format) {
      return Utils.formatTableValue(value, format);
    },
    async addTransfer() {
      const newTransfer = { TransfersInId: 0 };
      this.allElements.forEach((el) => {
        newTransfer[el.FieldName] = el.Value;
      });
      this.formElements.forEach((el) => {
        newTransfer[el.FieldName] = el.Value;
      });
      newTransfer.TransfersInId = 0;
      newTransfer.MembershipID = this.memberId;
      try {
        await this.$api.post('/TransfersIn/UpdateTransfersIn', newTransfer);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Transfer was added',
        });
        this.getTransfersList();
      } catch (err) {
        console.log(err.message);
      }
    },
    async updateTransfer() {
      this.loading = true;
      try {
        this.formElements.forEach((el) => {
          this.selectedTransfer[el.FieldName] = el.Value;
        });
        await this.$api.post('/TransfersIn/UpdateTransfersIn', this.selectedTransfer);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Transfer was updated',
        });
        this.getTransfersList();
      } catch (err) {
        console.log(err.message);
      }
    },
    async deleteTransfer() {
      this.loading = true;
      try {
        await this.$api.delete(
          '/TransfersIn/DeleteTransfersIn?Id=' + this.selectedTransfer.TransfersInId
        );

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Transfer was deleted',
        });
        this.getTransfersList();
      } catch (err) {
        console.log(err.message);
      }
    },
    async getTransfersList() {
      this.loading = true;
      try {
        let response = await this.$api.get(
          '/TransfersIn/GetTransfersInList?membershipId=' + this.memberId + '&orderBy=DateReceived'
        );
        this.transfersList = response.data;
        this.loading = false;
      } catch (err) {
        console.error(err);
      }
    },
    async getTransferById(id) {
      try {
        let response = await this.$api.get('/TransfersIn/GetTransfersIn?Id=' + id);

        if (!response.data) {
          const msg = 'Can not find transfer with id ' + id;
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        this.selectedTransfer = response.data;
        var data = response.data;
        // match data with form fields
        if (data) Utils.setDataToFormElements(data, this.formElements);
      } catch (err) {
        console.error(err);
      }
    },
    async getForm(formName) {
      try {
        let response = await this.$api.get(
          `/datadictionary/GetFormElements?formName=${formName}&planId=${this.currentPlanId}`
        );
        this.allElements = response.data;
        if (response.data.length)
          this.formElements = response.data.filter((f) => f.Visible == true);

        this.getTransfersList();
      } catch (err) {
        console.error(err);
      }
    },
    async getHeaders(formName) {
      try {
        const response = await this.$api.get(
          `/datadictionary/GetGridHeaders?formName=${formName}&planId=${this.currentPlanId}`
        );

        if (response.data.length) {
          const data = response.data.filter((item) => item.Visible);
          data.sort((a, b) => a.ColumbOrder - b.ColumbOrder);
          this.headers = data.map((item) => ({
            text: item.HeaderText,
            value: item.Datafield,
            align: item.Align.toLowerCase(),
            format: item.FieldFormat,
          }));
        }
      } catch (err) {
        console.log(err.message);
      }
    },
  },
};
</script>

<style scoped>
.transfersIn {
  margin-top: 5px;
  margin-bottom: 10px;
  padding: 5px 30px;
}
.sidebar {
  margin-bottom: 30px;
}

.sidebar h3 {
  margin-bottom: 20px;
}

.btnsContainer {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: white;
  padding: 10px;
  display: flex;
  justify-content: space-between;
}
.btnsContainer > div {
  display: inline-block;
}
</style>
